import {
  Column,
  FormCheckBox,
  FormInputField,
  FormRadioGroupV2,
  FormRadioItemV2,
  ModalBase,
  Row,
  Typography,
} from '@/components'
import { WithInfoText } from '@/components/hoc/WithInfoText'
import { ComponentColors } from '@/enums'
import { UnderlinedButtonLink } from '@/pages/CheckoutPage/styles'
import { useFormContext } from '@/providers'
import { useModal } from '@/utils'
import { Button, Input } from '@surein/ui'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const StyledInput = styled(Input)`
  margin-top: 0;
  cursor: pointer;
`

const ModalContent = styled(Column)`
  margin-top: 32px;
  gap: 16px;
`

const AddPaymentModal = ({
  isOpen,
  onClose,
  handleAddPaymentSubmit,
  isLoading,
  hideDefaultPaymentCheckbox,
}: {
  isOpen: boolean
  onClose: () => void
  handleAddPaymentSubmit: () => void
  isLoading?: boolean
  hideDefaultPaymentCheckbox?: boolean
}) => {
  const { t } = useTranslation()

  return (
    <ModalBase closable isOpen={isOpen} onClose={onClose} padding="16px">
      <ModalContent>
        <Typography bold variant="itemTitle">
          {t('checkout.paymentAndCompanyDetails.addPaymentMethod')}
        </Typography>
        <WithInfoText
          content={t('checkout.paymentAndCompanyDetails.accountNameInfo')}
          variant="secondary"
          gap="12px"
        >
          <FormInputField
            name="newPaymentName"
            label={t('checkout.paymentAndCompanyDetails.accountName')}
            className="mt-0 w-full"
          />
        </WithInfoText>
        <WithInfoText
          content={t('checkout.paymentAndCompanyDetails.ibanInfo')}
          variant="secondary"
          gap="12px"
        >
          <FormInputField
            name="newPaymentIban"
            label={t('checkout.paymentAndCompanyDetails.iban')}
            className="mt-0 w-full"
          />
        </WithInfoText>
        {!hideDefaultPaymentCheckbox && (
          <FormCheckBox
            name="newPaymentIsDefault"
            label={t('checkout.paymentAndCompanyDetails.isDefault')}
            noMargin
          />
        )}
        <Column gap="24px">
          <Button
            variant="primary"
            shape="square"
            onClick={handleAddPaymentSubmit}
            loading={isLoading}
          >
            {t('checkout.paymentAndCompanyDetails.save')}
          </Button>
          <Button variant="muted" onClick={onClose} shape="square">
            {t('checkout.paymentAndCompanyDetails.cancel')}
          </Button>
        </Column>
      </ModalContent>
    </ModalBase>
  )
}

interface Props {
  payments: {
    iban: string
    name: string
    paymentId: string
  }[]
  onCreatePaymentSubmit: (callback: () => void) => void
  isLoading?: boolean
  hideDefaultPaymentCheckbox?: boolean
  showEditButton?: boolean
}

export const PaymentMethodList = ({
  payments,
  onCreatePaymentSubmit,
  isLoading,
  hideDefaultPaymentCheckbox = false,
  showEditButton = false,
}: Props) => {
  const { t } = useTranslation()
  const { values, setFieldValue, errors, setFieldError } = useFormContext<{
    payment: { paymentId: string }
  }>()

  const { open, close, isOpen } = useModal()

  const handleInputClick = (paymentId: string) => {
    setFieldValue('payment.paymentId', paymentId)
  }

  const handleAddPaymentSubmit = () => {
    setFieldError('payment.paymentId', '')
    onCreatePaymentSubmit(close)
  }

  return (
    <Column gap="16px">
      <FormRadioGroupV2
        name="payment.paymentId"
        className={errors.payment?.paymentId ? 'input-error-styles' : ''}
      >
        {payments.map((payment) => (
          <label htmlFor={payment.paymentId} key={payment.paymentId}>
            <Row alignItems="center" gap="16px">
              <FormRadioItemV2
                key={payment.paymentId}
                value={payment.paymentId}
                name="paymentId"
                size="lg"
              />
              <StyledInput
                label={payment.name ?? ''}
                value={payment.iban}
                readOnly
                onClick={() => handleInputClick(payment.paymentId)}
                isSuccessful={values.payment?.paymentId === payment.paymentId}
              />
            </Row>
          </label>
        ))}
      </FormRadioGroupV2>
      <Row justifyContent="end">
        <UnderlinedButtonLink
          onClick={open}
          size="small"
          color={ComponentColors.primary}
        >
          {t(
            showEditButton
              ? 'checkout.paymentAndCompanyDetails.editPaymentMethod'
              : 'checkout.paymentAndCompanyDetails.addPaymentMethod',
          )}
        </UnderlinedButtonLink>
      </Row>
      <AddPaymentModal
        isOpen={isOpen}
        onClose={close}
        handleAddPaymentSubmit={handleAddPaymentSubmit}
        isLoading={isLoading}
        hideDefaultPaymentCheckbox={hideDefaultPaymentCheckbox}
      />
    </Column>
  )
}
